import { useState, useCallback, useEffect, useMemo } from 'react';

import { useFeatureFlag } from 'context/FeatureFlagContext';
import { FileDropZone } from 'features/common-elements/FileDropZone';
import { useS3Uploader } from 'features/common-elements/useS3Uploader';
import toast from 'react-hot-toast';
import { Dropdown } from 'ui-kit';
import type { DropdownOption } from 'ui-kit/Dropdown/Dropdown';

import { AssetClass, FileType, ProductType, QmMode } from '__generated__/globalTypes';
import { Card, CardHeading, CardInstructions } from '../styles';
import TapeUploadStepper from '../TapeUploadStepper';
import { ASK_FOR_TAPE_ROLE } from '../tapestry/errorMessages';
import { AssetClassOptionType } from './AssetClassOptionType';
import UploadHistory, { InProgressFile } from '../components/UploadHistory';

function getUploadErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const graphQLErrors = (
      error as { graphQLErrors: Array<{ message: string; extensions?: { code?: string } }> }
    ).graphQLErrors;
    if (graphQLErrors?.length > 0) {
      const gqlError = graphQLErrors[0];
      if (
        gqlError.extensions?.code === 'FORBIDDEN' ||
        gqlError.message.includes('Insufficient role')
      ) {
        return `You don\u2019t have permission to upload loan tapes. ${ASK_FOR_TAPE_ROLE}`;
      }
      return gqlError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Error uploading file. Please try again.';
}

const ALLOWED_MIME_TYPES = [
  'application/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/x-csv',
  'text/comma-separated-values',
  'text/csv',
  'text/tab-separated-values',
  'text/x-comma-separated-values',
  'text/x-csv',
];

const getAssetsLabelName = (value: string) => {
  switch (value) {
    case AssetClass.HOME:
      return 'Resi';
    default:
      return value;
  }
};

export const assetClassOptions: AssetClassOptionType[] = Object.values(AssetClass).map(
  (assetClass) => ({
    value: assetClass,
    label: getAssetsLabelName(assetClass.toString()).toUpperCase(),
  }),
);

function useAssetClassAndProductType() {
  const [selectedAssetClass, setSelectedAssetClass] = useState<AssetClass>();
  const [productType, setProductType] = useState<ProductType>(ProductType.FIXED);
  // Seller-declared QM mode for residential tapes (ENG-2826/2841). Defaults to
  // All QM; the backend also defaults absent qm-mode to QM, so this is a no-op
  // signal until a seller picks otherwise.
  const [qmMode, setQmMode] = useState<QmMode>(QmMode.QM);

  const handleAssetClassSelect = useCallback((selectedOption: { value: string; label: string }) => {
    setSelectedAssetClass(selectedOption.value as AssetClass);
  }, []);

  const handleProductTypeSelect = useCallback(
    (selectedOption: { value: string; label: string }) => {
      if (selectedOption?.value) {
        setProductType(selectedOption.value as ProductType);
      }
    },
    [],
  );

  const handleQmModeSelect = useCallback((selectedOption: { value: string; label: string }) => {
    if (selectedOption?.value) {
      setQmMode(selectedOption.value as QmMode);
    }
  }, []);

  useEffect(() => {
    setProductType(ProductType.FIXED);
    setQmMode(QmMode.QM);
  }, [selectedAssetClass]);

  return {
    selectedAssetClass,
    handleAssetClassSelect,
    productType,
    handleProductTypeSelect,
    qmMode,
    handleQmModeSelect,
  };
}

const QM_MODE_LABELS: Record<QmMode, string> = {
  [QmMode.QM]: 'All QM',
  [QmMode.NON_QM]: 'All Non-QM',
  [QmMode.MIXED]: 'Mixed (map a Non-QM flag column)',
};

type Props = {
  /**
   * Upload progress state + its updaters live in TapeUploadFlow (above the
   * list/detail split) so an in-flight upload's row survives navigating to a
   * tape's detail page and back; this component only renders and feeds them.
   */
  uploadsInProgress: InProgressFile[];
  onUploadStart: (file: File, assetClass: AssetClass | undefined, productType: ProductType) => void;
  onUploadSuccess: (file: File, versionId?: string, uuid?: string) => void;
  onUploadProgress: (progress: number, file?: File) => void;
  onUploadError: (file: File) => void;
};

export default function LoanTapeUploader({
  uploadsInProgress,
  onUploadStart,
  onUploadSuccess,
  onUploadProgress,
  onUploadError,
}: Props) {
  const isCREEnabled = useFeatureFlag('creEnabled');
  const isHelocEnabled = useFeatureFlag('helocEnabled');
  const isMarineEnabled = useFeatureFlag('marineEnabled');

  const ENABLED_ASSET_CLASSES = useMemo(() => {
    return [
      AssetClass.HOME,
      AssetClass.AUTO,
      AssetClass.UNSECPERSONAL,
      ...(isCREEnabled ? [AssetClass.CRE] : []),
      ...(isHelocEnabled ? [AssetClass.HELOC] : []),
      ...(isMarineEnabled ? [AssetClass.MARINE] : []),
    ];
  }, [isCREEnabled, isHelocEnabled, isMarineEnabled]);

  const {
    selectedAssetClass,
    handleAssetClassSelect,
    productType,
    handleProductTypeSelect,
    qmMode,
    handleQmModeSelect,
  } = useAssetClassAndProductType();

  const { upload } = useS3Uploader({
    fileType: FileType.LOAN_TAPE,
    onSuccess: onUploadSuccess,
    onProgress: onUploadProgress,
    assetClass: selectedAssetClass,
    productType,
    // qm-mode only applies to residential tapes; backend ignores it for other
    // asset classes (it's only stamped/read for HOME).
    qmMode: selectedAssetClass === AssetClass.HOME ? qmMode : undefined,
    allowedMimeTypes: ALLOWED_MIME_TYPES,
  });

  const onFileDrop = async (file: File) => {
    // Seed the optimistic row immediately (before the first byte) so it shows in
    // the table as soon as the file is dropped.
    onUploadStart(file, selectedAssetClass, productType);
    try {
      await upload(file);
    } catch (error) {
      // Retire the optimistic row — the upload never produced a tape — and name
      // the file in the error so it's clear which upload failed.
      onUploadError(file);
      const message = getUploadErrorMessage(error);
      toast.error(`Couldn't upload "${file.name}" — ${message}`, { duration: 6000 });
      console.error('Error uploading file:', error);
    }
  };

  const productTypeOptions = Object.values(ProductType).map((productType) => ({
    value: productType,
    label: productType.toString(),
  }));

  const qmModeOptions: DropdownOption[] = Object.values(QmMode).map((mode) => ({
    value: mode,
    label: QM_MODE_LABELS[mode],
  }));

  const isHome = selectedAssetClass === AssetClass.HOME;

  // The reveal block clips with overflow-hidden so the grid-rows 0fr↔1fr
  // animation can hide/show the Resi sub-params. But the QM-mode dropdown is the
  // last row, and its menu opens *below* the block — overflow-hidden would clip
  // it (reads as the menu hiding behind the table). So once the expand settles,
  // switch to overflow-visible to let the menu escape; clamp back to hidden the
  // moment we collapse so the close animation still clips.
  const [revealSettled, setRevealSettled] = useState(false);
  useEffect(() => {
    if (!isHome) {
      setRevealSettled(false);
      return;
    }
    // Under prefers-reduced-motion the grid expands instantly (no transition),
    // so don't hold overflow-hidden for the animation window — settle now, or a
    // menu opened in that gap would render clipped. Otherwise wait just past the
    // 200ms reveal before letting menus overflow the container.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setRevealSettled(true);
      return;
    }
    const timer = setTimeout(() => setRevealSettled(true), 240);
    return () => clearTimeout(timer);
  }, [isHome]);

  const shouldShowDropSection =
    selectedAssetClass && (selectedAssetClass === AssetClass.HOME ? productType : true);

  const filteredAssetClassOptions = useMemo(() => {
    return assetClassOptions
      .map((option) => ({
        ...option,
        isDisabled: !ENABLED_ASSET_CLASSES.includes(option.value),
      }))
      .sort((a, b) => {
        if (a.isDisabled && !b.isDisabled) {
          return 1;
        }
        if (!a.isDisabled && b.isDisabled) {
          return -1;
        }
        return 0;
      });
  }, [ENABLED_ASSET_CLASSES]);

  const selectedOption = filteredAssetClassOptions.find(
    (option) => option.value === selectedAssetClass,
  );

  return (
    <Card data-testid="tape-upload-card">
      <CardHeading>Upload Your Tapes</CardHeading>
      <TapeUploadStepper currentStep={1} onStepClick={() => {}} />
      <CardInstructions>
        Upload one or all of your loan tapes for evaluation. Supported file types include .csv,
        .xls, and .xlsx.
      </CardInstructions>
      {/* Labeled parameter column left, drop strip right. The params visibly feed
          the drop target (Gestalt grouping); Resi grows the column and the strip
          stretches with it. No reserved dead space — the strip is simply disabled
          until the parameters are complete. */}
      <div className="mb-4 mt-2 flex items-stretch gap-4">
        <div className="flex w-[400px] shrink-0 flex-col gap-2">
          {/* Asset class is the parent choice; the Resi sub-params below are
              refinements of it. Brighter + medium-weight caption promotes this
              to the section header so the two levels don't read as peers. */}
          <div className="text-xs font-medium uppercase tracking-wide text-slate-300">
            Asset class
            <Dropdown
              placeholder="Select asset class for this upload..."
              options={filteredAssetClassOptions}
              onValueChange={handleAssetClassSelect}
              value={getAssetsLabelName(selectedOption?.value || '').toUpperCase()}
              className="mt-1 w-full"
              data-testid="tape-upload-asset-class-dropdown"
            />
          </div>
          {/* Progressive-disclosure reveal: grid-rows 0fr→1fr animates the
              expansion without height measuring, so the strip and the table
              below slide instead of jumping (200ms ease-out, the consensus
              budget for medium reveals). Selects stay mounted — Dropdowns
              keep state and the reveal is pure CSS. motion-reduce gets the
              instant version. */}
          <div
            className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
              isHome ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
            aria-hidden={!isHome}
          >
            {/* `invisible` when collapsed: overflow clipping alone leaves the
                selects keyboard-focusable (Tab lands on invisible controls);
                visibility:hidden removes them from the tab order too.
                ml-1/border-l/pl-4 nest the Resi refinements behind a vertical
                rule so they visibly hang off the asset-class choice above
                (the inset also narrows the controls, reinforcing the
                parent→child relationship). */}
            <div
              className={`ml-1 flex min-h-0 flex-col gap-2 border-l border-slate-700 pl-4 ${
                revealSettled ? 'overflow-visible' : 'overflow-hidden'
              } ${isHome ? '' : 'invisible'}`}
            >
              <div className="text-xs uppercase tracking-wide text-slate-400">
                Product
                <Dropdown
                  options={productTypeOptions}
                  onValueChange={handleProductTypeSelect}
                  value={productType}
                  placeholder="Select product category..."
                  className="mt-1 w-full"
                />
              </div>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                QM mode
                <Dropdown
                  options={qmModeOptions}
                  onValueChange={handleQmModeSelect}
                  value={QM_MODE_LABELS[qmMode]}
                  placeholder="Select QM mode..."
                  className="mt-1 w-full"
                  data-testid="tape-upload-qm-mode-dropdown"
                />
              </div>
            </div>
          </div>
        </div>
        {/* fill (not a fixed height): the row is items-stretch, so this column is
            stretched to the parameter column's height — the strip then fills it,
            keeping its bottom edge aligned with the last dropdown for any asset
            class without a magic-number height. */}
        <div className="min-w-0 flex-1 text-[14px] text-white">
          <FileDropZone
            onFileDrop={onFileDrop}
            compact
            fill
            disabled={!shouldShowDropSection}
            allowedTypesMessage={
              shouldShowDropSection ? undefined : 'Select an asset class to enable upload'
            }
          />
        </div>
      </div>
      <UploadHistory uploadsInProgress={uploadsInProgress} />
    </Card>
  );
}
